import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import { getStaanderPositionName } from "entities/configurator/model/layoutChoiceName";
import {
  isEquallySpacedLayoutCode,
  isNewLayoutCode,
  isUHandleLengthWarningLayoutCode,
} from "entities/configurator/model/layoutMeta";
import classNames from "classnames";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Delete } from "shared/ui/icons/Delete";
import Text from "shared/ui/typography/Text";
import { concatClassnames } from "shared/utils/concatClassnames";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import { generateUUID } from "three/src/math/MathUtils";
import { BeamData } from "../../../app/providers/DoorOptionsProvider.types";
import { OptionTemplate } from "../../../shared/api/middleware-api-client";
import FormGroup from "../../../shared/ui/FormGroup";
import Icon, { IconTypes } from "../../../shared/ui/icon";
import Input from "../../../shared/ui/input";
import Label from "../../../shared/ui/label";
import MeasurementOptionControls from "../measurements-option-controls";
import { useScreenSize } from "app/hooks/shared/useScreenSize";
import { useSnapshot } from "valtio";
import { store } from "shared/store/store";
import { disableLayoutChanged } from "shared/store/layout/layoutStore";

// Slice last two elements from array
const takeRight = require("lodash.takeright");

export const maxHeightHandles = [
  "verticalhandletaatsschuif",
  "verticalhandletaatsschuifdubbel",
  "verticalhandle",
  "verticalhandlehout",
];

const doorConfigsToFlip = [
  "taatsdeur1paneel",
  "scharnierdeur1paneel",
  "dubbel1paneel",
  "dubbeleschuifdeur1paneel",
];

interface EigenwijsProps {
  showMeasurementInputs?: boolean;
}
const EigenwijsOptionControls = ({
  showMeasurementInputs = true,
}: EigenwijsProps) => {
  const {
    selectedDoor,
    layout,
    handle,
    color,
    glass,
    eigenwijs,
    minBeamCount,
    updateEigenwijs,
    calculateNewBeamHeight,
    selfAssembleDoorData,
    updateAnchorBeams,
    getDefaultLayoutBeamData,
    updateBeamHeightValidationError,
    staanderPosition,
    staanderOption,
  } = useConfigurationOptionsContext();

  const [searchParams] = useSearchParams();
  const [currentLayout, setCurrentLayout] = useState<OptionTemplate | null>(
    null,
  );
  const [eigenwijsIsLoading, setEigenwijsIsLoading] = useState<boolean>(true);

  const [beams, setBeams] = useState<BeamData[]>([]);
  const [beamFormInput, setBeamFormInput] = useState<string[]>([]);
  const [beamFormErrored, setBeamFormErrored] = useState<boolean[]>([]);
  const height = parseInt(searchParams.get("height") || "0");
  const [areMeasurementInputsHidden, hideMeasurementInputs] = useState(true);

  const isSpecialLayout = !!layout && isNewLayoutCode(layout.code);
  // These layouts warn about the U-handle length instead of blocking the design.
  const validatesUHandleBeamGap = !isUHandleLengthWarningLayoutCode(
    layout?.code,
  );

  const layoutSnap = useSnapshot(store);
  const layoutChanged = layoutSnap.layoutChanged;

  const minBeamOption = useMemo(() => {
    const option = [selectedDoor, color, glass, handle].sort(
      (a, b) => (b?.minBeamCount || 0) - (a?.minBeamCount || 0),
    )[0];

    // remove 'houten kozijn' from the option name
    if (option && option.name.includes("houten kozijn")) {
      return {
        ...option,
        name: option.name.replace("houten kozijn", ""),
      };
    }
    return option;
  }, [selectedDoor, color, glass, handle]);

  useEffect(() => {
    if (eigenwijsIsLoading) {
      return;
    }

    if (!currentLayout) {
      setCurrentLayout(layout);
      return;
    }

    if (layout?.code !== "eigenwijs" && currentLayout.code === "eigenwijs") {
      setEigenwijsIsLoading(true);
    }
  }, [layout]);

  useEffect(() => {
    if (isSpecialLayout) {
      setEigenwijsIsLoading(false);
      return;
    }
    if (!eigenwijsIsLoading || !eigenwijs || !eigenwijs.beams) {
      return;
    }

    let updatedBeam = false;

    if (layout?.code !== "eigenwijs") {
      updatedBeam = true;
    } else if (beams.length === eigenwijs.beams.length) {
      updatedBeam = true;
    }

    if (updatedBeam) {
      setEigenwijsIsLoading(false);
    }
  }, [isSpecialLayout, eigenwijs, eigenwijs?.beams, eigenwijs?.sizeObjects]);

  useEffect(() => {
    if (!eigenwijs) return;

    const newBeams = eigenwijs.beams.map((beamData) => ({ ...beamData }));
    const newBeamInputs = newBeams.map((beamData) =>
      beamData.height.toString(),
    );

    // Check if the lengths are different or any element is different
    if (
      beams.length !== newBeams.length ||
      beams.some((beam, index) => beam.height !== newBeams[index].height)
    ) {
      const updatedBeams = updateAnchorBeams(height, newBeams);
      setBeams(updatedBeams);
      setBeamFormInput(newBeamInputs);
    }
  }, [eigenwijs?.beams]);

  useEffect(() => {
    if (!layout) {
      return;
    }

    if (!eigenwijs) {
      return;
    }

    if (eigenwijsIsLoading) return;
    const newEigenwijsOptions = { ...eigenwijs };

    if (layout.code === "eigenwijs") {
      newEigenwijsOptions.beams = updateAnchorBeams(height, beams);
    } else if (isEquallySpacedLayoutCode(layout.code)) {
      newEigenwijsOptions.beams = getDefaultLayoutBeamData(height, beams);
    }

    updateEigenwijs(newEigenwijsOptions);
  }, [height, beams]);

  useEffect(() => {
    initializeBeamHeightError();
  }, [beamFormInput, handle]);

  const initializeBeamHeightError = () => {
    updateBeamHeightValidationError(
      beamFormInput.reduce((prev, beam, id) => {
        return prev || checkHeightError(id);
      }, false),
    );
  };

  const validateBeamByIndex = (
    beamsArray: BeamData[],
    index: number,
  ): boolean => {
    const beamToCheck = beamsArray[index];
    let isValid = false;

    beamsArray.forEach((value, i) => {
      if (index === i) {
        if (
          handle &&
          handle.code.includes("normalhandleschuif") &&
          value.height <= 1100 &&
          value.height >= 1290
        ) {
          isValid = true;
        }
      } else if (Math.abs(beamToCheck.height - value.height) < 150) {
        isValid = true;
      }
    });

    return isValid;
  };

  const checkHeightError = (index: number): boolean => {
    if (layoutChanged) {
      return false;
    }
    if (
      validatesUHandleBeamGap &&
      maxHeightHandles.includes(handle?.code ?? "")
    ) {
      if (beams.length >= 2) {
        const lastTwo = takeRight(beams, 2);

        if (Math.abs(lastTwo[0].height - lastTwo[1].height) > 2100) {
          if (lastTwo[0].id === beams[index].id) {
            return true;
          }
        }
      }
    }

    return false;
  };

  const updatedBeam = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    disableLayoutChanged();
    const newBeamValueString: string = e.target.value;
    const newBeamValue: number = parseInt(newBeamValueString);

    let allBeams = [...beams];
    let allBeamInputs = [...beamFormInput];
    allBeams[index] = { ...allBeams[index], height: newBeamValue };
    allBeamInputs[index] = newBeamValueString;

    setBeamFormInput(allBeamInputs);
  };

  const onBeamInputFocus = (id: string) => {
    if (eigenwijs) {
      const newEigenwijs = { ...eigenwijs, selectedBeam: id };
      updateEigenwijs(newEigenwijs);
    }
  };

  const onBeamInputBlur = (index: number) => {
    if (eigenwijs) {
      const newEigenwijs = { ...eigenwijs, selectedBeam: null };
      updateEigenwijs(newEigenwijs);
    }
    let allBeams = [...beams];

    let beamFormValues = [...beamFormInput];
    beamFormValues.sort((a, b) => parseInt(b) - parseInt(a));

    allBeams = allBeams.map((beam, i) => {
      let height = parseInt(beamFormValues[i]);
      if (hasBeamInvalidValue(index)) {
        height = beam.height;
      }
      return {
        ...beam,
        height: height,
      };
    });

    setBeams(allBeams);
    setBeamFormInput(allBeams.map((value) => value.height.toString()));
  };

  const addBeam = () => {
    let allBeams = [...beams];
    const beamHeight: number = calculateNewBeamHeight(beams, height);

    allBeams.push({
      id: generateUUID(),
      adjustable: true,
      height: beamHeight,
      handleAnchor: false,
    });
    allBeams.sort((a, b) => b.height - a.height);

    let allBeamInputs = allBeams.map((value) => value.height.toString());

    setBeams(allBeams);
    setBeamFormInput(allBeamInputs);
  };

  const removeBeam = (index: number) => {
    let allBeams = [...beams];
    let allBeamInputs = [...beamFormInput];
    let allBeamErrored = [...beamFormErrored];

    allBeams.splice(index, 1);
    allBeamInputs.splice(index, 1);
    allBeamErrored.splice(index, 1);

    allBeamErrored.forEach((isInvalid, erroredBeamIndex) => {
      if (!isInvalid) return;
      allBeamErrored[erroredBeamIndex] = validateBeamByIndex(
        allBeams,
        erroredBeamIndex,
      );
    });

    setBeamFormErrored(allBeamErrored);

    setBeams(allBeams);
    setBeamFormInput(allBeamInputs);
  };

  const checkBeam150MMDifference = () => {
    const values = beamFormInput.map(Number).sort((a, b) => a - b);
    return values.some((val, i) => i > 0 && val - values[i - 1] < 150);
  };

  const hasBeamInvalidValue = (index: number): boolean => {
    if (parseInt(beamFormInput[index]) > height - 200) {
      toast.error("Maximale hoogte: " + (height - 200) + " mm", {
        toastId: "maximalehoogte",
      });
      return true;
    }
    if (parseInt(beamFormInput[index]) < 200) {
      toast.error("Minimale hoogte: 200 mm", { toastId: "under200" });
      return true;
    }
    if (
      handle &&
      handle.code.includes("normalhandleschuif") &&
      parseInt(beamFormInput[index]) > 1100 &&
      parseInt(beamFormInput[index]) < 1290
    ) {
      toast.error("Ligger mag niet tussen 1100 mm en 1290 mm bij deze greep", {
        toastId: "11001290",
      });
      return true;
    }
    if (checkBeam150MMDifference()) {
      toast.error("Minimale afstand tussen liggers is 150 mm", {
        toastId: "beam-form-errored",
      });
      return true;
    }

    if (
      validatesUHandleBeamGap &&
      maxHeightHandles.includes(handle?.code ?? "")
    ) {
      if (beamFormInput.length >= 2) {
        const lastTwo = takeRight(beamFormInput, 2);
        if (Math.abs(lastTwo[0] - lastTwo[1]) > 2100) {
          toast.error(
            "Let op: De U-greep kan niet worden toegepast in de huidige configuratie.  U-grepen kunnen tot een maximale lengte van 2100mm worden gemaakt.  Voeg een extra ligger toe of maak de afstand tussen de huidige liggers kleiner.",
            { toastId: "2100-error-beam" },
          );
          return true;
        }
      }
    }
    return false;
  };

  const handleValidationTypeBeam = (index: number): string => {
    if (parseInt(beamFormInput[index]) > height - 200) {
      return "Maximale hoogte: " + (height - 200) + " mm";
    }
    if (parseInt(beamFormInput[index]) < 200) {
      return "Minimale hoogte: 200 mm";
    } else if (
      handle &&
      handle.code.includes("normalhandleschuif") &&
      parseInt(beamFormInput[index]) > 1100 &&
      parseInt(beamFormInput[index]) < 1290
    ) {
      return "Ligger mag niet tussen 1100 mm en 1290 mm bij deze greep";
    } else if (beamFormErrored[index]) {
      return "Minimale afstand tussen liggers is 150 mm";
    } else if (checkHeightError(index)) {
      toast.error(
        "Let op: De U-greep kan niet worden toegepast in de huidige configuratie.  U-grepen kunnen tot een maximale lengte van 2100mm worden gemaakt.  Voeg een extra ligger toe of maak de afstand tussen de huidige liggers kleiner.",
        { toastId: "2100-error-beam" },
      );
      return "";
    }
    return "";
  };

  const getErrorMessage = (
    type: string,
    value: number,
    index: number = -1,
    selectedDoor?: OptionTemplate | null,
  ): string => {
    if (isNaN(value)) {
      return "Dit veld is verplicht";
    } else if (type === "beam") {
      return handleValidationTypeBeam(index);
    }
    return "";
  };

  const { isMobileScreen } = useScreenSize();
  const shouldReduceMotion = useReducedMotion();

  if (!layout) {
    return <></>;
  }

  const staanderPrice =
    staanderPosition && staanderOption ? staanderOption.price : 0;
  const choicePrice = layout.price + staanderPrice;
  const currentChoiceName = staanderPosition
    ? `${layout.name} + ${getStaanderPositionName(staanderPosition)}`
    : layout.name;
  const staanderLayoutState = staanderOption
    ? (staanderPosition ?? "available")
    : "hidden";

  return (
    <div
      className={classNames(
        "gg-w-full",
        layout.code !== "eigenwijs" && "gg-max-w-screen-sm",
      )}
    >
      {layout.code !== "eigenwijs" && (
        <>
          {!isMobileScreen && (
            <div
              data-testid="layout-options-price-container"
              className="gg-mb-5 gg-mt-4 gg-bg-grey-500 gg-p-4 gg-text-sm gg-text-dark-blue"
            >
              <header className="gg-mb-2 gg-flex gg-justify-between">
                <Text className="gg-font-bold">
                  Huidige keuze: {currentChoiceName}
                </Text>
                <Text
                  data-testid="formatted-price"
                  className="gg-shrink-0 gg-text-secondary-text"
                >
                  +{getFormattedPrice(choicePrice)}
                </Text>
              </header>
              <Text className="gg-min-h-[1lh] gg-transition-opacity gg-duration-300 gg-ease-in-out">
                {layout.description}
              </Text>
            </div>
          )}

          {showMeasurementInputs && (
            <button
              data-testid="measurement-inputs-button"
              onClick={() => hideMeasurementInputs(!areMeasurementInputsHidden)}
              className={concatClassnames(
                "gg-group gg-w-full gg-text-secondary-text gg-transition-all hover:gg-text-zinc-900",
                "gg-text-sm gg-font-[500] active:gg-text-dark-blue-900",
                "gg-flex gg-items-center gg-rounded-full",
              )}
            >
              <Icon
                name={IconTypes.MEASUREMENT}
                alt=""
                className={classNames(
                  "gg-mr-2.5 gg-transition gg-duration-500 gg-ease-in-out group-hover:-gg-translate-x-0.5",
                  {
                    "-gg-translate-x-0.5": !areMeasurementInputsHidden,
                  },
                )}
              />
              <span>Pas de maten in het voorbeeld aan</span>
              <div className="gg-ml-auto">
                <div className="gg-grid gg-aspect-square gg-w-7 gg-place-items-center gg-rounded-full gg-border gg-border-[#BFBEC8] gg-bg-[#F4F4F6]">
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    size="sm"
                    className={classNames(
                      "gg-transition gg-duration-500 gg-ease-in-out",
                      {
                        "gg-rotate-180": !areMeasurementInputsHidden,
                        "group-hover:gg-rotate-180": areMeasurementInputsHidden,
                      },
                    )}
                  />
                </div>
              </div>
            </button>
          )}
        </>
      )}
      <AnimatePresence initial={false}>
        {(layout.code === "eigenwijs" || !areMeasurementInputsHidden) && (
          <motion.div
            layout={
              layout.code === "eigenwijs" && !shouldReduceMotion
                ? "position"
                : false
            }
            layoutDependency={staanderLayoutState}
            transition={{
              layout: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
            }}
            className={concatClassnames(
              "gg-w-full",
              layout.code !== "eigenwijs" && "gg-pt-4",
            )}
            initial={"collapsed"}
            animate="open"
            exit="collapsed"
            variants={{
              open: {
                height: "auto",
                opacity: 1,
                transition: { duration: 0.385, ease: "linear" },
              },
              collapsed: {
                height: 0,
                opacity: 0,
                transition: { duration: 0.3, ease: "linear" },
              },
            }}
          >
            {layout.code !== "eigenwijs" && (
              <button
                className="gg-mb-5 gg-ml-auto gg-block gg-text-sm gg-font-bold gg-underline gg-underline-offset-4 hover:gg-text-gray-500"
                onClick={() => hideMeasurementInputs(true)}
              >
                Sluiten
              </button>
            )}

            {layout.code === "eigenwijs" && (
              <div
                data-testid="layout-options-price-container"
                className="gg-mb-5 gg-mt-10 gg-bg-grey-500 gg-p-4 gg-text-sm gg-text-dark-blue"
              >
                <header className="gg-mb-2 gg-flex gg-justify-between">
                  <Text className="gg-font-bold">
                    Huidige keuze: {currentChoiceName}
                  </Text>
                  <Text
                    data-testid="formatted-price"
                    className="gg-shrink-0 gg-text-secondary-text"
                  >
                    +{getFormattedPrice(choicePrice)}
                  </Text>
                </header>
                <Text>
                  {selfAssembleDoorData.selfAssembly
                    ? "Stel je eigen ontwerp samen. Voeg liggers toe of laat ze weg en bepaal zelf de afstanden tussen de liggers door de maten hieronder in te voeren. De afmeting die je hier invult, is de hartmaat van een ligger."
                    : layout.description}
                </Text>
              </div>
            )}

            {!selfAssembleDoorData.selfAssembly && (
              <>
                <Text className="gg-mb-4 gg-text-sm">
                  Wil je een beter beeld krijgen hoe jouw deur er uit kan zien?
                  Stel dan de maten in, dan passen wij het voorbeeld hier op
                  aan!
                </Text>

                <MeasurementOptionControls />

                <Text className="gg-mt-3 gg-text-sm">
                  De maten nemen wij mee in de bestelling, maar worden bij het
                  inmeten natuurlijk nagekeken en aangepast waar nodig!
                </Text>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {layout.code === "eigenwijs" && (
        <div className="gg-mt-10 gg-grid gg-w-full gg-grid-cols-12 gg-gap-5">
          <FormGroup className="gg-col-span-12 gg-grid gg-gap-y-7">
            {beamFormInput.map((beam, index) => (
              <div
                key={`beam-from-input-${index}`}
                className="gg-grid gg-w-full gg-grid-cols-1 gg-gap-3 sm:gg-grid-cols-2 sm:gg-gap-5 lg:gg-grid-cols-1 xl:gg-grid-cols-2"
              >
                <Label
                  htmlFor={`width-form-input-${index}`}
                  className="gg-flex gg-min-w-fit gg-flex-col gg-text-sm gg-font-bold gg-text-zinc-800"
                >
                  {`Ligger #${index + 1}`}
                  <span className="gg-block gg-text-sm gg-font-normal gg-text-zinc-400">
                    Afstand vanaf de vloer gemeten
                  </span>
                </Label>

                <div className="gg-flex gg-w-full gg-gap-4">
                  <Input
                    id={`width-form-input-${index}`}
                    data-testid={`door-beam-${index}`}
                    className="sm:gg-pr-12 xl:gg-pr-5"
                    type={"number"}
                    placeholder="Ligger"
                    value={beam}
                    onChange={(e) => updatedBeam(e, index)}
                    onFocus={() => onBeamInputFocus(beams[index].id)}
                    onBlur={() => onBeamInputBlur(index)}
                    suffix={"mm"}
                    error={
                      parseInt(beam) > height - 200 ||
                      parseInt(beam) < 200 ||
                      beamFormErrored[index] ||
                      beamFormInput[index] === "" ||
                      (handle?.code.includes("normalhandleschuif") &&
                        parseInt(beam) > 1100 &&
                        parseInt(beam) < 1290)
                    }
                    errorText={getErrorMessage("beam", parseInt(beam), index)}
                  />
                  {beams.length > minBeamCount && (
                    <div className="gg-items-start gg-justify-start gg-pt-2.5">
                      <button onClick={() => removeBeam(index)}>
                        <Delete />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </FormGroup>

          {minBeamCount !== 0 &&
            beams.length === minBeamCount &&
            minBeamOption !== null && (
              <Text className="gg-col-span-12 gg-mt-2">
                {`Een ${minBeamOption.name} is toe te passen bij minimaal ${minBeamCount} liggers.`}
              </Text>
            )}

          {beams.length < 5 && (
            <button
              data-testid={"door-add-beam-button"}
              onClick={addBeam}
              className="gg-col-span-12 gg-items-center gg-justify-center gg-whitespace-nowrap gg-rounded-full gg-border gg-border-solid gg-border-gray-200 gg-bg-gray-100 gg-py-2.5 gg-text-center gg-text-sm gg-font-normal gg-leading-6 gg-text-slate-800 hover:gg-border-gray-300 hover:gg-bg-gray-200 hover:gg-text-black active:gg-outline-none active:gg-ring-1 active:gg-ring-gray-300"
            >
              Ligger toevoegen
            </button>
          )}
        </div>
      )}
    </div>
  );
};
export default EigenwijsOptionControls;
